import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const PostHogVariantSchema = z.object({
  key: z.string(),
  rollout_percentage: z.number(),
});

const SyncFromPostHogRequestSchema = z.object({
  posthogFlagKey: z.string().min(1),
});

const SyncFromPostHogSuccessResponseSchema = z.object({
  flagKey: z.string(),
  flagName: z.string(),
  variants: z.array(PostHogVariantSchema),
});

const SyncFromPostHogErrorResponseSchema = z.object({
  error: z.string(),
});

const VariantConfigSchema = z.record(z.unknown());

const ExperimentWithVariantSchema = z.object({
  experimentId: z.string().uuid(),
  experimentName: z.string(),
  posthogFlagKey: z.string(),
  variantId: z.string().uuid(),
  variantName: z.string(),
  posthogVariantKey: z.string(),
  variantConfig: VariantConfigSchema,
  rank: z.number(),
});

const DryRunRequestSchema = z.object({
  tenantId: z.string().uuid(),
  userId: z.string().uuid(),
});

const DryRunResponseSchema = z.object({
  experiment: ExperimentWithVariantSchema.nullable(),
  reason: z.enum([
    'assigned',
    'not_in_experiment',
    'no_active_experiments',
    'posthog_unavailable',
  ]),
});

const AssignedExperimentSchema = z.object({
  experimentId: z.string().uuid(),
  experimentName: z.string(),
  variantId: z.string().uuid(),
  variantName: z.string(),
  resolvedConfig: VariantConfigSchema,
});

const SetAppointmentExperimentRequestSchema = z.object({
  tenantId: z.string().uuid(),
  userId: z.string().uuid(),
});

const SetAppointmentExperimentResponseSchema = z.object({
  assigned: z.boolean(),
  experiment: AssignedExperimentSchema.nullable(),
});

const GetAssignedExperimentResponseSchema = z.object({
  experiment: AssignedExperimentSchema.nullable(),
});

const TestAssignmentMatchSchema = ExperimentWithVariantSchema.extend({
  isWinner: z.boolean(),
  isInjected: z.boolean(),
  dateStatus: z.enum(['active', 'not_started', 'expired']),
});

const TestAssignmentRequestSchema = z.object({
  tenantId: z.string().uuid(),
  userId: z.string().uuid(),
  injectExperimentId: z.string().uuid().optional(),
  /** Local time string without timezone (e.g., "2024-01-15T09:00:00") */
  simulatedLocalTime: z.string().optional(),
});

const TestAssignmentResponseSchema = z.object({
  allMatches: z.array(TestAssignmentMatchSchema),
  winner: ExperimentWithVariantSchema.nullable(),
  injectedExperiment: TestAssignmentMatchSchema.nullable(),
});

export const experimentsContract = c.router(
  {
    syncFromPostHog: {
      method: 'POST',
      path: '/sync-from-posthog',
      responses: {
        200: SyncFromPostHogSuccessResponseSchema,
        404: SyncFromPostHogErrorResponseSchema,
        500: SyncFromPostHogErrorResponseSchema,
      },
      body: SyncFromPostHogRequestSchema,
      summary: 'Fetch feature flag variants from PostHog',
    },
    dryRun: {
      method: 'POST',
      path: '/dry-run',
      responses: {
        200: DryRunResponseSchema,
      },
      body: DryRunRequestSchema,
      summary:
        'Evaluate which experiment a user would be assigned to (without writing to DB)',
    },
    setAppointmentExperiment: {
      method: 'POST',
      path: '/appointments/:appointmentId/assign',
      responses: {
        200: SetAppointmentExperimentResponseSchema,
      },
      body: SetAppointmentExperimentRequestSchema,
      summary: 'Assign an experiment to an appointment (write-once)',
    },
    getAssignedExperiment: {
      method: 'GET',
      path: '/appointments/:appointmentId',
      responses: {
        200: GetAssignedExperimentResponseSchema,
      },
      query: z.object({
        tenantId: z.string().uuid(),
      }),
      summary: 'Get the assigned experiment for an appointment',
    },
    testAssignment: {
      method: 'POST',
      path: '/test-assignment',
      responses: {
        200: TestAssignmentResponseSchema,
      },
      body: TestAssignmentRequestSchema,
      summary:
        'Test experiment assignment for a user. Shows all matching experiments and the winner.',
    },
  },
  {
    pathPrefix: '/experiments',
  },
);

export type PostHogVariant = z.infer<typeof PostHogVariantSchema>;
export type SyncFromPostHogSuccessResponse = z.infer<
  typeof SyncFromPostHogSuccessResponseSchema
>;
export type SyncFromPostHogErrorResponse = z.infer<
  typeof SyncFromPostHogErrorResponseSchema
>;
